import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CSSProperties } from "@mui/styles";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { IOption } from "../Dropdown";
import { SegmentFieldVariant, SegmentSelect } from "./SegmentSelect";

interface IFailureTypeFieldProps {
    machineCenterId: number;
    value?: number;
    obligatory?: boolean;
    inlineStyle?: CSSProperties;
    variant?: SegmentFieldVariant;
    placeholder?: string;
    onChange: (failureTypeId: number, isLocationSpecific: boolean) => void;
}

/**
 * Controlled failure-type selector reused by the inline table edit and the split
 * modal. onChange also reports the selected type's is_location_specific flag so the
 * caller can drive the area/functional-location cascade.
 */
export const FailureTypeField = ({
    machineCenterId,
    value,
    obligatory = true,
    inlineStyle,
    variant = "inline",
    placeholder,
    onChange,
}: IFailureTypeFieldProps) => {
    const { i18n } = useTranslation();
    const { loading, failureTypes } = EventReportsEntity.GetFailureTypesOptionsForEdition(machineCenterId);

    const options: IOption[] = useMemo(
        () =>
            failureTypes.map((failureType) => ({
                id: failureType.failure_type_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureType as unknown as IFailureType,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_TYPE,
                ),
            })),
        [failureTypes, i18n],
    );

    const currentSelectionInOptions = useMemo(() => options.some((option) => option.id === value), [options, value]);

    const handleSelect = (id: number) => {
        const failureTypeSelected = failureTypes.find((failureType) => failureType.failure_type_id === id);
        onChange(id, !!failureTypeSelected?.is_location_specific);
    };

    return (
        <SegmentSelect
            variant={variant}
            value={value}
            options={options}
            onChange={handleSelect}
            loading={loading}
            obligatory={obligatory}
            pending={!Boolean(value) || !currentSelectionInOptions}
            placeholder={placeholder}
            inlineStyle={inlineStyle}
        />
    );
};
